import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import { hourlyRateQuery, saveHourlyRate, type RateSubject } from '../../api/rates'
import { toast } from '../../ui/notify'
import { failed } from './shared'

/**
 * TARIFUL ORAR (numai v4) - un rand sub fisa contului si sub fisa grupului,
 * ca rolurile suplimentare de sub fisa contului. Din el iese costul pe
 * tehnician din Rapoarte (timpul lucrat al tichetelor x tariful tehnicianului,
 * altfel al grupului tichetului). Gol = fara tarif.
 *
 * Fara drept de citire pe conturi, randul nu apare deloc; fara drept de
 * editare, se vede numai cifra.
 */
export function HourlyRate({ type, id }: { type: RateSubject; id: number }) {
  const client = useQueryClient()
  const page = useQuery(hourlyRateQuery(type, id))
  const [rate, setRate] = useState('')
  const [currency, setCurrency] = useState('RON')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!page.data) return
    setRate(page.data.hourly_rate === null ? '' : String(page.data.hourly_rate))
    setCurrency(page.data.currency || 'RON')
  }, [page.data])

  if (page.error instanceof ApiError && (page.error.status === 403 || page.error.status === 404)) return null
  if (page.error) return <div className="alert alert-danger">{failed(page.error)}</div>
  if (!page.data) return null

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const value = rate.trim() === '' ? null : Number(rate.replace(',', '.'))
    if (value !== null && (!Number.isFinite(value) || value < 0)) {
      toast('The hourly rate must be a positive number.', 'Error', 'error')
      return
    }
    setBusy(true)
    void saveHourlyRate(type, id, value, currency)
      .then((out) => {
        client.setQueryData(hourlyRateQuery(type, id).queryKey, out)
        toast(value === null ? 'Hourly rate removed' : 'Hourly rate saved', 'Success')
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <section className="hourly-rate" aria-label="Hourly rate settings">
      <h5 className="mt-3">Hourly rate</h5>
      <p className="text-muted small mb-2">
        {type === 'user'
          ? 'Used for the cost per technician in Reports: time worked on tickets × this rate. Empty = the rate of the ticket group.'
          : 'Used for the cost per technician in Reports when the technician has no rate of their own.'}
      </p>
      {page.data.may_edit ? (
        <form className="form-inline mb-3" onSubmit={submit}>
          <input
            className="form-control form-control-sm mr-2"
            type="number"
            min={0}
            step="0.01"
            inputMode="decimal"
            aria-label="Hourly rate"
            placeholder="none"
            value={rate}
            style={{ width: 120 }}
            onChange={(e) => setRate(e.target.value)}
          />
          <input
            className="form-control form-control-sm mr-2"
            aria-label="Currency"
            maxLength={3}
            value={currency}
            style={{ width: 70 }}
            onChange={(e) => setCurrency(e.target.value.toUpperCase())}
          />
          <span className="text-muted small mr-2">/ hour</span>
          <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>
            {busy ? 'Saving...' : 'Save rate'}
          </button>
        </form>
      ) : (
        <p data-testid="hourly-rate-value">{page.data.hourly_rate === null ? 'No rate' : `${page.data.hourly_rate} ${page.data.currency} / hour`}</p>
      )}
    </section>
  )
}
